package gr.tech.meetinggenie.components.widgets

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.BoxShadow
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.PointerEvents
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.TransitionTimingFunction
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.bottom
import com.varabyte.kobweb.compose.ui.modifiers.boxShadow
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.left
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.opacity
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.pointerEvents
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.right
import com.varabyte.kobweb.compose.ui.modifiers.top
import com.varabyte.kobweb.compose.ui.modifiers.transition
import com.varabyte.kobweb.compose.ui.modifiers.zIndex
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.icons.fa.FaCheck
import com.varabyte.kobweb.silk.components.icons.fa.IconSize
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.meetinggenie.styles.Theme
import kotlinx.browser.window
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.ms
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

// Button style
val CopyStringBoxStyle = CssStyle {
    base {
        Modifier
            .padding(leftRight = 12.px, topBottom = 6.px)
            .border(1.px, LineStyle.Solid, Colors.Grey)
            .borderRadius(4.px)
            .color(Colors.Black)
            .fontSize(1.cssRem)
            .cursor(Cursor.Pointer)
            .boxShadow(BoxShadow.of(0.px,3.px,5.px, color = Color.rgba(0,0,0,50)))
    }
    hover {
        Modifier
            .transition(Transition.all(100.ms))
            .backgroundColor(Theme.MediumGreen.rgb)
            .color(Colors.White)
    }
}

// Container relative positioning so overlay can center
val CopyContainerStyle = CssStyle {
    base {
        Modifier
            .position(Position.Relative)
            .minHeight(40.px)
    }
}

// Overlay base: absolute, centered, pointer-events none
val CopiedOverlayBase = CssStyle {
    base {
        Modifier
            .position(Position.Absolute)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .display(DisplayStyle.Flex)
            .justifyContent(JustifyContent.Center)
            .alignItems(AlignItems.Center)
            .fontWeight(FontWeight.Bold)
            .zIndex(1)
            .pointerEvents(PointerEvents.None)
            .backgroundColor(Colors.White)
            .color(Colors.Black)
            .transition(Transition.of("opacity", 300.ms, TransitionTimingFunction.EaseInOut))
    }
}

// Overlay hidden: transparent
val CopiedOverlayHidden = CssStyle { base { Modifier.opacity(0) } }

// Overlay visible: green background, white text
val CopiedOverlayVisible = CssStyle {
    base {
        Modifier
            .opacity(1)
            .backgroundColor(Theme.MediumGreen.rgb)
            .color(Colors.White)
    }
}

@Composable
fun CopyTextButton(string: String, modifier: Modifier = Modifier) {
    var clicked by remember { mutableStateOf(false) }

    P(
        attrs = CopyStringBoxStyle.toModifier()
            .then(CopyContainerStyle.toModifier())
            .fillMaxWidth()
            .then(modifier)
            .onClick {
                window.navigator.clipboard.writeText(string)
                clicked = true
                // Fade out after 1s
                window.setTimeout({ clicked = false }, 1000)
            }
            .toAttrs()
    ) {
        // Button content
        Div {
            SpanText(
                text = "Click to Copy: ",
                modifier = Modifier.fontWeight(FontWeight.Bold)
            )
            Text(string)
        }

        // Overlay: Copied! + check icon
        Div(
            attrs = CopiedOverlayBase.toModifier()
                .then(if (clicked) CopiedOverlayVisible.toModifier() else CopiedOverlayHidden.toModifier())
                .toAttrs()
        ) {
            Text("Copied!")
            FaCheck(
                size = IconSize.SM,
                modifier = Modifier.margin(left = 6.px).color(Colors.White)
            )
        }
    }
}